/* eslint-disable jsx-a11y/anchor-is-valid */
import type { FC } from "react";
import { useState, useEffect, useRef, useMemo, ChangeEvent } from "react";
import { createPortal } from "react-dom";
import { HiOutlinePlus, HiX, HiOutlineX } from "react-icons/hi";
import {
  StandardForm,
  StyledTextInput,
  StyledTextArea,
  CustomSelect,
  VatSelector,
  EditableDropdown,
  ToggleCard,
  Button,
} from "@components";
import {
  IProduct,
  IParameter,
  InputValidationResult,
  ParameterType,
} from "../../types";
import { useTranslation } from "react-i18next";
import { Product } from "../../models";
import {
  validateNumberBetween,
  hasValidationError,
} from "../../helpers/validationHelper";
import { IPriceModel } from "../../types/Product";
import { compressImage } from "../../utils/imageUtils";
import { safeParseFloat } from "../../utils/parseNumber";
import {
  modalSurface,
  mapOverlayHeader,
  formFooter,
  textHeading,
} from "@design-system";

export interface ProductModalProp {
  data: IProduct;
  productTypes: IParameter[];
  isShowing: boolean;
  onConfirm: (data: IProduct) => void;
  onCancel: () => void;
  showSaveToggle?: boolean;
  shouldSaveProduct?: boolean;
  onSaveToggleChange?: (shouldSave: boolean) => void;
}

// Forbedret custom modal-komponent som bruker React Portal
const CustomModal: FC<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}> = ({ isOpen, onClose, title, children }) => {
  // Opprett portal-target hvis det ikke finnes
  const [portalElement, setPortalElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    // Opprett portal-container hvis den ikke finnes
    if (isOpen) {
      let element = document.getElementById("product-modal-portal");
      if (!element) {
        element = document.createElement("div");
        element.id = "product-modal-portal";
        document.body.appendChild(element);
      }
      setPortalElement(element);

      // Fjern overflow på body når modal er åpen
      document.body.style.overflow = "hidden";

      // Cleanup
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [isOpen]);

  // Ikke render noe hvis modal er lukket eller portal ikke er klar
  if (!isOpen || !portalElement) return null;

  // Bruk createPortal for å rendre modal utenfor normal DOM-flyt
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-gray-900 bg-opacity-50 transition-opacity">
      <div
        className={`relative w-full max-w-3xl max-h-[90vh] ${modalSurface} overflow-hidden animate-in fade-in duration-300`}
      >
        {/* Header */}
        <div
          className={`flex justify-between items-center p-6 pb-2 ${mapOverlayHeader}`}
        >
          <h3 className={`text-xl font-medium ${textHeading}`}>{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-muted bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white"
          >
            <HiX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">{children}</div>
      </div>
    </div>,
    portalElement,
  );
};

// Runder til 2 desimaler for å unngå floating point-artifakter
const roundToTwoDecimals = (value: number): number => {
  if (!isFinite(value)) return 0;
  return Math.round(value * 100) / 100;
};

export const ProductForm: FC<ProductModalProp> = function (
  props: ProductModalProp,
) {
  const { t } = useTranslation(["common", "inventory", "modals"]);
  const [seatCount, setSeatCount] = useState(
    props.data.seatsEnabled ? (props.data.seatCount ?? 0) : 0,
  );
  const seatsEnabled = seatCount > 0;
  const [seatPrice, setSeatPrice] = useState(
    props.data.pricing?.pricePerSeat ?? 0,
  );
  const [isNew] = useState(Product.isNew(props.data));
  const selectableProductTypes = useMemo(() => {
    if (props.productTypes.length > 0) {
      const hasStandard = props.productTypes.some(
        (type) => type.code.trim().toLowerCase() === "standard",
      );

      // Present the legacy default category as Standard until its stored code
      // has been migrated, while keeping the existing category id intact.
      return props.productTypes.map((type) =>
        !hasStandard && type.code.trim().toLowerCase() === "box"
          ? { ...type, code: "Standard" }
          : type,
      );
    }

    // Keep product creation usable while an older workspace has no categories.
    return [
      {
        id: "Standard",
        workspaceId: props.data.workspaceId ?? "",
        code: "Standard",
        type: ParameterType.ProductType,
      },
    ];
  }, [props.data.workspaceId, props.productTypes]);
  const defaultProductType =
    props.data.productType?.[0] ??
    (Product.isNew(props.data)
      ? (selectableProductTypes.find(
          (type) => type.code.trim().toLowerCase() === "standard",
        )?.id ?? selectableProductTypes[0]?.id)
      : undefined);
  const [pricingModels, setPricingModels] = useState<IPriceModel[]>(
    props.data.pricing?.priceModel ??
      (Product.isNew(props.data) ? [IPriceModel.FIXED] : []),
  );
  const [productType, setProductType] = useState<string[]>(
    defaultProductType ? [defaultProductType] : [],
  );
  const [isValidated, setIsValidated] = useState(false);
  const [validations, setValidations] = useState([] as InputValidationResult[]);
  const [formData, setFormData] = useState<any>({});
  const [vatPercentage, setVatPercentage] = useState<number>(
    props.data.pricing?.vat ?? 25,
  );
  const [images, setImages] = useState<string[]>(props.data.imgUrls || []);
  const [isPublic, setIsPublic] = useState<boolean>(
    props.data.isPublic ?? false,
  );
  const [pricesIncludeVat, setPricesIncludeVat] = useState<boolean>(
    props.data.pricing?.pricesIncludeVat ?? false,
  );

  // Bruk en ref for å holde toggle-tilstanden i stedet for state
  const saveProductRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Initialiser toggle-tilstanden
  useEffect(() => {
    if (saveProductRef.current) {
      saveProductRef.current.checked = props.shouldSaveProduct ?? true;
    }
  }, [props.shouldSaveProduct]);

  // Oppdater productType når produktet eller de tilgjengelige kategoriene endres
  useEffect(() => {
    if (props.data.productType && props.data.productType.length > 0) {
      setProductType(props.data.productType);

      // Oppdater også formData med produkttypen
      setFormData((prevData: Record<string, any>) => ({
        ...prevData,
        productType: props.data.productType,
      }));
    } else if (Product.isNew(props.data) && defaultProductType) {
      setProductType([defaultProductType]);
      setFormData((prevData: Record<string, any>) => ({
        ...prevData,
        productType: [defaultProductType],
      }));
    }

    // Oppdater bilder når props.data endres
    setImages(props.data.imgUrls || []);

    // Sync isPublic with props.data
    setIsPublic(props.data.isPublic ?? false);
  }, [props.data, defaultProductType]);

  // Bildehåndteringsfunksjoner
  const onImageSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (files.length) {
      let newImageList = [...images];
      for (const file of files) {
        try {
          // Komprimere bildet før lagring (max 600x400, 70% kvalitet)
          const compressedUrl = await compressImage(file, 600, 400, 0.7);
          newImageList.push(compressedUrl);
        } catch (error) {
          console.error("Error compressing image:", error);
          // Fallback til original hvis komprimering feiler
          try {
            const reader = new FileReader();
            reader.onload = (e) => {
              if (e.target?.result) {
                newImageList.push(e.target.result as string);
                setImages([...newImageList]);
              }
            };
            reader.readAsDataURL(file);
          } catch (fallbackError) {
            console.error("Error reading file:", fallbackError);
          }
        }
      }
      setImages(newImageList);
    }
  };

  const removeImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
  };

  const getValidationResults = (product: IProduct) => {
    // Sjekk om descriptionShort er gyldig - ikke valider hvis vi er i redigeringsmodus og feltet ikke er endret
    const isDescriptionShortValid =
      product.descriptionShort !== undefined &&
      product.descriptionShort !== null &&
      typeof product.descriptionShort === "string" &&
      product.descriptionShort.trim() !== "" &&
      product.descriptionShort.trim().length >= 2;

    // Sjekk om productType er gyldig
    const isProductTypeValid =
      product.productType !== undefined &&
      Array.isArray(product.productType) &&
      product.productType.length > 0;

    return [
      {
        id: "descriptionShort",
        show: isValidated,
        isInvalid: !isDescriptionShortValid,
        errorMessage: t("common:errors.fields.not_empty"),
      },
      {
        id: "productType",
        show: isValidated,
        isInvalid: !isProductTypeValid,
        errorMessage: t("common:errors.fields.not_empty"),
      },
      {
        id: "weight",
        show: isValidated,
        isInvalid:
          product.weight !== undefined &&
          product.weight !== null &&
          product.weight !== 0 &&
          !validateNumberBetween(Number(product.weight), 0, 10000),
        errorMessage: t("common:errors.fields.number"),
      },
      {
        id: "height",
        show: isValidated,
        isInvalid:
          product.height !== undefined &&
          product.height !== null &&
          product.height !== 0 &&
          !validateNumberBetween(Number(product.height), 0, 10000),
        errorMessage: t("common:errors.fields.number"),
      },
      {
        id: "width",
        show: isValidated,
        isInvalid:
          product.width !== undefined &&
          product.width !== null &&
          product.width !== 0 &&
          !validateNumberBetween(Number(product.width), 0, 10000),
        errorMessage: t("common:errors.fields.number"),
      },
      {
        id: "length",
        show: isValidated,
        isInvalid:
          product.length !== undefined &&
          product.length !== null &&
          product.length !== 0 &&
          !validateNumberBetween(Number(product.length), 0, 10000),
        errorMessage: t("common:errors.fields.number"),
      },
      {
        id: "pricing.priceModel",
        show: isValidated,
        isInvalid:
          product.pricing === undefined ||
          product.pricing.priceModel === undefined ||
          product.pricing.priceModel.length === 0,
        errorMessage: t("common:errors.fields.not_empty"),
      },
    ];
  };

  // Sikre at productType alltid er et array
  const ensureProductTypeIsArray = (
    productType: string | string[] | undefined | null,
  ): string[] => {
    if (!productType) return [];
    if (Array.isArray(productType)) return productType;
    return [productType];
  };

  const handleFormSubmit = (submitted: Record<string, any> = {}) => {
    // Sikre at descriptionShort er trimmet og ikke tom
    const rawDescriptionShort =
      submitted.descriptionShort ??
      formData.descriptionShort ??
      props.data.descriptionShort ??
      "";
    const trimmedDescriptionShort =
      typeof rawDescriptionShort === "string"
        ? rawDescriptionShort.trim()
        : String(rawDescriptionShort ?? "").trim();

    // Sikre at numeriske verdier er tall
    const weight =
      submitted.weight !== undefined
        ? safeParseFloat(submitted.weight)
        : formData.weight !== undefined
          ? safeParseFloat(formData.weight)
          : props.data.weight;
    const length =
      submitted.length !== undefined
        ? safeParseFloat(submitted.length)
        : formData.length !== undefined
          ? safeParseFloat(formData.length)
          : props.data.length;
    const height =
      submitted.height !== undefined
        ? safeParseFloat(submitted.height)
        : formData.height !== undefined
          ? safeParseFloat(formData.height)
          : props.data.height;
    const width =
      submitted.width !== undefined
        ? safeParseFloat(submitted.width)
        : formData.width !== undefined
          ? safeParseFloat(formData.width)
          : props.data.width;

    // Get VAT percentage from submitted data, existing state, or use current state
    const vat =
      submitted["pricing.vat"] !== undefined
        ? safeParseFloat(submitted["pricing.vat"])
        : formData["pricing.vat"] !== undefined
          ? safeParseFloat(formData["pricing.vat"])
          : vatPercentage;

    // Helper function to convert VAT-inclusive price to net price
    const convertToNetPrice = (
      price: number,
      vatRate: number,
      includesVat: boolean,
    ): number => {
      if (!includesVat || vatRate <= 0) return roundToTwoDecimals(price);
      return roundToTwoDecimals(price / (1 + vatRate / 100));
    };
    // Håndter priser - convert to net prices if they include VAT
    let pricePerCargoUnit =
      submitted["pricing.pricePerCargoUnit"] !== undefined
        ? convertToNetPrice(
            safeParseFloat(submitted["pricing.pricePerCargoUnit"]),
            vat,
            pricesIncludeVat,
          )
        : props.data.pricing?.pricePerCargoUnit !== undefined
          ? roundToTwoDecimals(props.data.pricing.pricePerCargoUnit)
          : undefined;

    let pricePerDistanceUnit =
      submitted["pricing.pricePerDistanceUnit"] !== undefined
        ? convertToNetPrice(
            safeParseFloat(submitted["pricing.pricePerDistanceUnit"]),
            vat,
            pricesIncludeVat,
          )
        : props.data.pricing?.pricePerDistanceUnit !== undefined
          ? roundToTwoDecimals(props.data.pricing.pricePerDistanceUnit)
          : undefined;

    let pricePerVolumeUnit =
      submitted["pricing.pricePerVolumeUnit"] !== undefined
        ? convertToNetPrice(
            safeParseFloat(submitted["pricing.pricePerVolumeUnit"]),
            vat,
            pricesIncludeVat,
          )
        : props.data.pricing?.pricePerVolumeUnit !== undefined
          ? roundToTwoDecimals(props.data.pricing.pricePerVolumeUnit)
          : undefined;

    let pricePerWeightUnit =
      submitted["pricing.pricePerWeightUnit"] !== undefined
        ? convertToNetPrice(
            safeParseFloat(submitted["pricing.pricePerWeightUnit"]),
            vat,
            pricesIncludeVat,
          )
        : props.data.pricing?.pricePerWeightUnit !== undefined
          ? roundToTwoDecimals(props.data.pricing.pricePerWeightUnit)
          : undefined;

    let pricePerTimeUnit =
      submitted["pricing.pricePerTimeUnit"] !== undefined
        ? convertToNetPrice(
            safeParseFloat(submitted["pricing.pricePerTimeUnit"]),
            vat,
            pricesIncludeVat,
          )
        : props.data.pricing?.pricePerTimeUnit !== undefined
          ? roundToTwoDecimals(props.data.pricing.pricePerTimeUnit)
          : undefined;

    const timePeriodInMinutes =
      submitted["pricing.timePeriodInMinutes"] !== undefined
        ? safeParseFloat(submitted["pricing.timePeriodInMinutes"])
        : formData["pricing.timePeriodInMinutes"] !== undefined
          ? safeParseFloat(formData["pricing.timePeriodInMinutes"])
          : (props.data.pricing?.timePeriodInMinutes ?? 60);

    const selectedModels = Array.isArray(pricingModels) ? pricingModels : [];
    if (!selectedModels.includes(IPriceModel.FIXED)) {
      pricePerCargoUnit = 0;
    }
    if (!selectedModels.includes(IPriceModel.DISTANCE)) {
      pricePerDistanceUnit = 0;
    }
    if (!selectedModels.includes(IPriceModel.VOLUME)) {
      pricePerVolumeUnit = 0;
    }
    if (!selectedModels.includes(IPriceModel.WEIGHT)) {
      pricePerWeightUnit = 0;
    }
    if (!selectedModels.includes(IPriceModel.TIME)) {
      pricePerTimeUnit = 0;
    }

    // Håndter produkttype
    let finalProductType = productType;
    if (submitted.productType) {
      finalProductType = Array.isArray(submitted.productType)
        ? submitted.productType
        : [submitted.productType];
    } else if (formData.productType) {
      finalProductType = Array.isArray(formData.productType)
        ? formData.productType
        : [formData.productType];
    } else if (!finalProductType || finalProductType.length === 0) {
      finalProductType = props.data.productType || [];
    }

    const product = {
      ...props.data,
      descriptionShort: trimmedDescriptionShort,
      description:
        submitted.description ??
        formData.description ??
        props.data.description ??
        "",
      seatsEnabled,
      seatCount: seatsEnabled ? seatCount : undefined,
      // Konverter numeriske verdier til tall
      weight,
      length,
      height,
      width,
      // Sett produkttype
      productType: finalProductType,
      // Set public status
      isPublic: isPublic,
      pricing: {
        ...props.data.pricing,
        priceModel: pricingModels,
        pricePerSeat: seatsEnabled ? seatPrice : undefined,
        pricePerCargoUnit,
        pricePerDistanceUnit,
        pricePerVolumeUnit,
        pricePerWeightUnit,
        pricePerTimeUnit,
        timePeriodInMinutes,
        vat,
        pricesIncludeVat,
      },
    } as IProduct;
    validateAndConfirm(product);
  };

  const validateAndConfirm = (product: IProduct) => {
    // Trigger validering
    const validationResults = getValidationResults(product);
    setValidations(validationResults);
    // Sjekk om det er valideringsfeil
    if (hasValidationError(validationResults)) {
      return;
    }

    // Hent toggle-status
    const isToggleChecked = saveProductRef.current?.checked || false;

    // Informer parent om toggle-status hvis nødvendig
    if (props.onSaveToggleChange) {
      props.onSaveToggleChange(isToggleChecked);
    }

    // Konverter numeriske verdier til tall
    const finalProduct: IProduct = {
      ...product,
      weight: safeParseFloat(product.weight),
      length: safeParseFloat(product.length),
      height: safeParseFloat(product.height),
      width: safeParseFloat(product.width),
      productType: ensureProductTypeIsArray(product.productType),
      imgUrls: images,
      isPublic: product.isPublic,
      pricing: {
        ...product.pricing,
        priceModel: product.pricing?.priceModel || [],
        pricePerCargoUnit: safeParseFloat(product.pricing?.pricePerCargoUnit),
        pricePerVolumeUnit: safeParseFloat(product.pricing?.pricePerVolumeUnit),
        pricePerWeightUnit: safeParseFloat(product.pricing?.pricePerWeightUnit),
        pricePerDistanceUnit: safeParseFloat(
          product.pricing?.pricePerDistanceUnit,
        ),
        pricePerTimeUnit: safeParseFloat(product.pricing?.pricePerTimeUnit),
        timePeriodInMinutes: safeParseFloat(
          product.pricing?.timePeriodInMinutes ?? 60,
        ),
        vat: safeParseFloat(product.pricing?.vat),
        pricesIncludeVat: product.pricing?.pricesIncludeVat ?? false,
      },
    };

    props.onConfirm(finalProduct);
  };

  useEffect(() => {
    setIsValidated(true);
  }, [setValidations, validations]);

  // Bygger feltene for skjemaet
  const fields = [
    {
      id: "descriptionShort",
      label: t("inventory:product_name"),
      grid_style: "col-span-2",
      input: (
        <StyledTextInput
          id="descriptionShort"
          placeholder={t("inventory:product_description_example")}
          defaultValue={props.data.descriptionShort ?? ""}
          validation={validations.find(
            (validation) => validation.id === "descriptionShort",
          )}
          onInputChanged={(value) => {
            // Oppdater formData direkte når brukeren skriver
            setFormData((prevData: Record<string, any>) => {
              return {
                ...prevData,
                descriptionShort: value,
              };
            });
          }}
        />
      ),
    },
    {
      id: "description",
      label: t("inventory:product_description"),
      grid_style: "col-span-2",
      input: (
        <StyledTextArea
          id="description"
          defaultValue={props.data.description ?? ""}
          placeholder={t("modals:placeholders.text_area")}
          onInputChanged={(value) => {
            // Oppdater formData direkte når brukeren skriver
            setFormData((prevData: Record<string, any>) => {
              return {
                ...prevData,
                description: value,
              };
            });
          }}
        />
      ),
    },
    {
      id: "seatCount",
      label: t("inventory:seats.count"),
      input: (
        <StyledTextInput
          id="seatCount"
          type="number"
          min={pricingModels.includes(IPriceModel.SEAT) ? 1 : 0}
          max={10000}
          step={1}
          required={pricingModels.includes(IPriceModel.SEAT)}
          placeholder="0"
          defaultValue={
            props.data.seatsEnabled ? (props.data.seatCount ?? "") : ""
          }
          onInputChanged={(value) => setSeatCount(Number(value))}
        />
      ),
    },
    {
      id: "weight",
      label: t("common:weight"),
      input: (
        <div className="relative">
          <StyledTextInput
            id="weight"
            placeholder={t("common:placeholder.weight")}
            defaultValue={
              props.data.weight && props.data.weight > 0
                ? props.data.weight
                : ""
            }
            type="number"
            validation={validations.find(
              (validation) => validation.id === "weight",
            )}
            onInputChanged={(value) => {
              // Oppdater formData direkte når brukeren skriver
              setFormData((prevData: Record<string, any>) => {
                const numValue = value === "" ? 0 : parseFloat(value);
                return {
                  ...prevData,
                  weight: numValue,
                };
              });
            }}
          />
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
            Kilo
          </span>
        </div>
      ),
    },
    {
      id: "length",
      label: t("common:length"),
      input: (
        <div className="relative">
          <StyledTextInput
            id="length"
            placeholder={t("common:placeholder.length")}
            defaultValue={
              props.data.length && props.data.length > 0
                ? props.data.length
                : ""
            }
            type="number"
            validation={validations.find(
              (validation) => validation.id === "length",
            )}
            onInputChanged={(value) => {
              // Oppdater formData direkte når brukeren skriver
              setFormData((prevData: Record<string, any>) => {
                const numValue = value === "" ? 0 : parseFloat(value);
                return {
                  ...prevData,
                  length: numValue,
                };
              });
            }}
          />
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
            cm
          </span>
        </div>
      ),
    },
    {
      id: "height",
      label: t("common:height"),
      input: (
        <div className="relative">
          <StyledTextInput
            id="height"
            placeholder={t("common:placeholder.height")}
            defaultValue={
              props.data.height && props.data.height > 0
                ? props.data.height
                : ""
            }
            type="number"
            validation={validations.find(
              (validation) => validation.id === "height",
            )}
            onInputChanged={(value) => {
              // Oppdater formData direkte når brukeren skriver
              setFormData((prevData: Record<string, any>) => {
                const numValue = value === "" ? 0 : parseFloat(value);
                return {
                  ...prevData,
                  height: numValue,
                };
              });
            }}
          />
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
            cm
          </span>
        </div>
      ),
    },
    {
      id: "width",
      label: t("common:width"),
      input: (
        <div className="relative">
          <StyledTextInput
            id="width"
            placeholder={t("common:placeholder.width")}
            defaultValue={
              props.data.width && props.data.width > 0 ? props.data.width : ""
            }
            type="number"
            validation={validations.find(
              (validation) => validation.id === "width",
            )}
            onInputChanged={(value) => {
              // Oppdater formData direkte når brukeren skriver
              setFormData((prevData: Record<string, any>) => {
                const numValue = value === "" ? 0 : parseFloat(value);
                return {
                  ...prevData,
                  width: numValue,
                };
              });
            }}
          />
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
            cm
          </span>
        </div>
      ),
    },
    {
      id: "productType",
      label: t("common:category"),
      tooltip: t("common:productType_tooltip"),
      input: (
        <CustomSelect
          key={`product-type-${defaultProductType}-${selectableProductTypes
            .map((type) => type.id)
            .join("-")}`}
          type="radio"
          defaultValue={defaultProductType ?? ""}
          label={t("modals:placeholders.category")}
          onSelectChanged={(value) => {
            setProductType([value] as string[]);
            // Oppdater formData direkte når brukeren velger
            setFormData((prevData: Record<string, any>) => {
              return {
                ...prevData,
                productType: [value],
              };
            });
          }}
          items={selectableProductTypes.map((type) => ({
            key: type.id,
            label: type.code,
            value: type.id,
          }))}
          validation={validations.find(
            (validation) => validation.id === "productType",
          )}
        />
      ),
    },
    {
      id: "pricing.priceModel",
      label: t("common:priceModel_label"),
      tooltip: t("common:priceModel_tooltip"),
      input: (
        <CustomSelect
          type="checkbox"
          defaultValue={pricingModels}
          label={t("modals:placeholders.price_model")}
          onSelectChanged={(values) => {
            const newPricingModels = values as IPriceModel[];
            const oldPricingModels = pricingModels || [];

            // Only clear pricing values for removed models
            const removedModels = oldPricingModels.filter(
              (model) => !newPricingModels.includes(model),
            );

            if (removedModels.length > 0) {
              setFormData((prevData: Record<string, any>) => {
                const updatedData = { ...prevData };

                removedModels.forEach((model) => {
                  switch (model) {
                    case IPriceModel.FIXED:
                      updatedData["pricing.pricePerCargoUnit"] = 0;
                      break;
                    case IPriceModel.DISTANCE:
                      updatedData["pricing.pricePerDistanceUnit"] = 0;
                      break;
                    case IPriceModel.VOLUME:
                      updatedData["pricing.pricePerVolumeUnit"] = 0;
                      break;
                    case IPriceModel.WEIGHT:
                      updatedData["pricing.pricePerWeightUnit"] = 0;
                      break;
                    case IPriceModel.TIME:
                      updatedData["pricing.pricePerTimeUnit"] = 0;
                      updatedData["pricing.timePeriodInMinutes"] = 60;
                      break;
                  }
                });

                return updatedData;
              });
            }

            setPricingModels(newPricingModels);
          }}
          items={[
            {
              key: IPriceModel.SEAT,
              label: t("inventory:seats.rateModel"),
              value: IPriceModel.SEAT,
            },
            {
              key: IPriceModel.FIXED,
              label: t("common:price_model.fixed"),
              value: IPriceModel.FIXED,
            },
            {
              key: IPriceModel.VOLUME,
              label: t("common:price_model.volume"),
              value: IPriceModel.VOLUME,
            },
            {
              key: IPriceModel.WEIGHT,
              label: t("common:price_model.weight"),
              value: IPriceModel.WEIGHT,
            },
            {
              key: IPriceModel.DISTANCE,
              label: t("common:price_model.distance"),
              value: IPriceModel.DISTANCE,
            },
            {
              key: IPriceModel.TIME,
              label: t("common:price_model.time"),
              value: IPriceModel.TIME,
            },
          ]}
          validation={validations.find(
            (validation) => validation.id === "pricing.priceModel",
          )}
        />
      ),
    },
    {
      id: "pricing.pricePerSeat",
      label: t("inventory:seats.price"),
      hidden: !pricingModels.includes(IPriceModel.SEAT),
      input: (
        <StyledTextInput
          id="pricing.pricePerSeat"
          type="number"
          min={0.01}
          max={1000000}
          step={0.01}
          required={pricingModels.includes(IPriceModel.SEAT)}
          placeholder=""
          defaultValue={seatPrice || ""}
          onInputChanged={(value) => setSeatPrice(Number(value))}
        />
      ),
    },
    {
      id: "pricing.pricePerCargoUnit",
      label: t("common:price_model.fixed"),
      hidden: !pricingModels?.includes(IPriceModel.FIXED),
      input: (
        <div className="relative">
          <StyledTextInput
            id="pricing.pricePerCargoUnit"
            placeholder=""
            defaultValue={
              props.data.pricing?.pricePerCargoUnit &&
              props.data.pricing.pricePerCargoUnit > 0
                ? props.data.pricing.pricesIncludeVat &&
                  (props.data.pricing.vat ?? 0) > 0
                  ? roundToTwoDecimals(
                      props.data.pricing.pricePerCargoUnit *
                        (1 + (props.data.pricing.vat ?? 0) / 100),
                    )
                  : roundToTwoDecimals(props.data.pricing.pricePerCargoUnit)
                : ""
            }
            type="number"
            step={0.01}
            min={0}
            validation={validations.find(
              (validation) => validation.id === "pricing.pricePerCargoUnit",
            )}
            onInputChanged={(value) => {
              // Oppdater formData direkte når brukeren skriver
              setFormData((prevData: Record<string, any>) => {
                const numValue = value === "" ? 0 : parseFloat(value);
                return {
                  ...prevData,
                  "pricing.pricePerCargoUnit": numValue,
                };
              });
            }}
          />
          {vatPercentage > 0 && (
            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
              {pricesIncludeVat ? t("common:incl_vat") : t("common:excl_vat")}
            </span>
          )}
        </div>
      ),
    },
    {
      id: "pricing.pricePerDistanceUnit",
      label: t("common:price_model.distance"),
      hidden: !pricingModels?.includes(IPriceModel.DISTANCE),
      input: (
        <div className="relative">
          <StyledTextInput
            id="pricing.pricePerDistanceUnit"
            placeholder=""
            defaultValue={
              props.data.pricing?.pricePerDistanceUnit &&
              props.data.pricing.pricePerDistanceUnit > 0
                ? props.data.pricing.pricesIncludeVat &&
                  (props.data.pricing.vat ?? 0) > 0
                  ? roundToTwoDecimals(
                      props.data.pricing.pricePerDistanceUnit *
                        (1 + (props.data.pricing.vat ?? 0) / 100),
                    )
                  : roundToTwoDecimals(props.data.pricing.pricePerDistanceUnit)
                : ""
            }
            type="number"
            step={0.01}
            min={0}
            validation={validations.find(
              (validation) => validation.id === "pricing.pricePerDistanceUnit",
            )}
            onInputChanged={(value) => {
              // Oppdater formData direkte når brukeren skriver
              setFormData((prevData: Record<string, any>) => {
                const numValue = value === "" ? 0 : parseFloat(value);
                return {
                  ...prevData,
                  "pricing.pricePerDistanceUnit": numValue,
                };
              });
            }}
          />
          {vatPercentage > 0 && (
            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
              {pricesIncludeVat ? t("common:incl_vat") : t("common:excl_vat")}
            </span>
          )}
        </div>
      ),
    },
    {
      id: "pricing.pricePerVolumeUnit",
      label: t("common:price_model.volume"),
      hidden: !pricingModels?.includes(IPriceModel.VOLUME),
      input: (
        <div className="relative">
          <StyledTextInput
            id="pricing.pricePerVolumeUnit"
            placeholder=""
            defaultValue={
              props.data.pricing?.pricePerVolumeUnit &&
              props.data.pricing.pricePerVolumeUnit > 0
                ? props.data.pricing.pricesIncludeVat &&
                  (props.data.pricing.vat ?? 0) > 0
                  ? roundToTwoDecimals(
                      props.data.pricing.pricePerVolumeUnit *
                        (1 + (props.data.pricing.vat ?? 0) / 100),
                    )
                  : roundToTwoDecimals(props.data.pricing.pricePerVolumeUnit)
                : ""
            }
            type="number"
            step={0.01}
            min={0}
            validation={validations.find(
              (validation) => validation.id === "pricing.pricePerVolumeUnit",
            )}
            onInputChanged={(value) => {
              // Oppdater formData direkte når brukeren skriver
              setFormData((prevData: Record<string, any>) => {
                const numValue = value === "" ? 0 : parseFloat(value);
                return {
                  ...prevData,
                  "pricing.pricePerVolumeUnit": numValue,
                };
              });
            }}
          />
          {vatPercentage > 0 && (
            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
              {pricesIncludeVat ? t("common:incl_vat") : t("common:excl_vat")}
            </span>
          )}
        </div>
      ),
    },
    {
      id: "pricing.pricePerWeightUnit",
      label: t("common:price_model.weight"),
      hidden: !pricingModels?.includes(IPriceModel.WEIGHT),
      input: (
        <div className="relative">
          <StyledTextInput
            id="pricing.pricePerWeightUnit"
            placeholder=""
            defaultValue={
              props.data.pricing?.pricePerWeightUnit &&
              props.data.pricing.pricePerWeightUnit > 0
                ? props.data.pricing.pricesIncludeVat &&
                  (props.data.pricing.vat ?? 0) > 0
                  ? roundToTwoDecimals(
                      props.data.pricing.pricePerWeightUnit *
                        (1 + (props.data.pricing.vat ?? 0) / 100),
                    )
                  : roundToTwoDecimals(props.data.pricing.pricePerWeightUnit)
                : ""
            }
            type="number"
            step={0.01}
            min={0}
            validation={validations.find(
              (validation) => validation.id === "pricing.pricePerWeightUnit",
            )}
            onInputChanged={(value) => {
              // Oppdater formData direkte når brukeren skriver
              setFormData((prevData: Record<string, any>) => {
                const numValue = value === "" ? 0 : parseFloat(value);
                return {
                  ...prevData,
                  "pricing.pricePerWeightUnit": numValue,
                };
              });
            }}
          />
          {vatPercentage > 0 && (
            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
              {pricesIncludeVat ? t("common:incl_vat") : t("common:excl_vat")}
            </span>
          )}
        </div>
      ),
    },
    {
      id: "pricing.pricePerTimeUnit",
      label: t("common:price_model.time"),
      hidden: !pricingModels?.includes(IPriceModel.TIME),
      input: (
        <div className="relative">
          <StyledTextInput
            id="pricing.pricePerTimeUnit"
            placeholder=""
            defaultValue={
              props.data.pricing?.pricePerTimeUnit &&
              props.data.pricing.pricePerTimeUnit > 0
                ? props.data.pricing.pricesIncludeVat &&
                  (props.data.pricing.vat ?? 0) > 0
                  ? props.data.pricing.pricePerTimeUnit *
                    (1 + (props.data.pricing.vat ?? 0) / 100)
                  : props.data.pricing.pricePerTimeUnit
                : ""
            }
            type="number"
            validation={validations.find(
              (validation) => validation.id === "pricing.pricePerTimeUnit",
            )}
            onInputChanged={(value) => {
              // Oppdater formData direkte når brukeren skriver
              setFormData((prevData: Record<string, any>) => {
                const numValue = value === "" ? 0 : parseFloat(value);
                return {
                  ...prevData,
                  "pricing.pricePerTimeUnit": numValue,
                };
              });
            }}
          />
          {vatPercentage > 0 && (
            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
              {pricesIncludeVat ? t("common:incl_vat") : t("common:excl_vat")}
            </span>
          )}
        </div>
      ),
    },
    {
      id: "pricing.timePeriodInMinutes",
      label: t("common:time_period"),
      hidden: !pricingModels?.includes(IPriceModel.TIME),
      input: (
        <EditableDropdown
          value={props.data.pricing?.timePeriodInMinutes || 60}
          onChange={(value) => {
            setFormData((prevData: Record<string, any>) => ({
              ...prevData,
              "pricing.timePeriodInMinutes": value,
            }));
          }}
          placeholder={t("common:minutes_placeholder")}
          suffix={t("common:minutes", "minutter")}
          min={1}
          step={1}
          items={[
            {
              key: "1",
              label: t("common:1_minute"),
              value: "1",
            },
            {
              key: "10",
              label: t("common:10_minutes"),
              value: "10",
            },
            {
              key: "30",
              label: t("common:30_minutes"),
              value: "30",
            },
            {
              key: "60",
              label: t("common:60_minutes"),
              value: "60",
            },
            {
              key: "120",
              label: t("common:120_minutes"),
              value: "120",
            },
          ]}
        />
      ),
    },
    {
      id: "pricing.pricesIncludeVat",
      label: "",
      hidden: vatPercentage <= 0,
      grid_style: "col-span-2",
      input: (
        <div className="flex items-center space-x-3 py-2">
          <input
            type="checkbox"
            id="pricesIncludeVat"
            checked={pricesIncludeVat}
            onChange={(e) => setPricesIncludeVat(e.target.checked)}
            className="w-4 h-4 text-brand bg-gray-100 border-gray-300 rounded focus:ring-0 dark:bg-gray-700 dark:border-gray-600"
          />
          <label
            htmlFor="pricesIncludeVat"
            className="text-sm font-medium text-gray-900 dark:text-gray-300"
          >
            {t("common:price_includes_vat")}
          </label>
        </div>
      ),
    },
    {
      id: "isPublic",
      label: "",
      input: (
        <ToggleCard
          title={t("common:public_product")}
          description={
            isPublic
              ? t("common:public_product_enabled_description")
              : t("common:public_product_disabled_description")
          }
          isEnabled={isPublic}
          onToggle={setIsPublic}
        />
      ),
    },
    {
      id: "pricing.vat",
      label: t("common:vat_percentage"),
      tooltip: t("common:vat_percentage_description"),
      input: (
        <VatSelector
          initialValue={vatPercentage}
          presetValues={[12, 15, 25]}
          onChange={(value: number) => {
            setVatPercentage(value);
            setFormData((prevData: Record<string, any>) => ({
              ...prevData,
              "pricing.vat": value,
            }));
          }}
          labelText={undefined}
          descriptionText={t("common:vat_percentage_description")}
        />
      ),
    },

    {
      id: "images",
      label: "Bilder",
      grid_style: "col-span-2",
      input: (
        <div>
          {/* Existing Images */}
          {images.length > 0 && (
            <div className="grid grid-cols-4 gap-4 mb-4">
              {images.map((img, index) => (
                <div key={index} className="relative group">
                  <img
                    src={img}
                    alt={t("inventory:product_image_alt", { index: index + 1 })}
                    className="w-full h-20 object-cover rounded-md border border-gray-300"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <HiOutlineX className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Upload Button */}
          <div className="flex items-center justify-center w-full">
            <label
              htmlFor="image-upload"
              className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-surface-muted dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600"
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <HiOutlinePlus className="w-8 h-8 mb-2 text-muted" />
                <p className="mb-2 text-sm text-muted">
                  <span className="font-semibold">Klikk for å laste opp</span>{" "}
                  eller dra og slipp
                </p>
                <p className="text-xs text-muted">
                  PNG, JPG eller GIF (maks 10MB)
                </p>
              </div>
              <input
                id="image-upload"
                type="file"
                className="hidden"
                multiple
                accept="image/*"
                onChange={onImageSelected}
              />
            </label>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="overflow-auto max-h-[70vh] p-1">
      <form
        ref={formRef}
        onSubmit={(e) => {
          e.preventDefault();
          // Hent form data fra den indre formen som StandardForm rendrer
          const innerForm: HTMLFormElement | null =
            e.currentTarget.querySelector("form");
          const domFormData = new FormData(innerForm ?? e.currentTarget);
          const data: Record<string, any> = {};

          fields.forEach((field) => {
            // Bypass hidden fields, image field (handled separately), and fields not present as actual inputs
            if (field.hidden || field.id === "images") return;
            if (!domFormData.has(field.id)) return;

            const fieldId = field.id;
            let fieldData: any = domFormData.getAll(field.id);
            if (fieldData.length === 1) {
              fieldData = fieldData[0];
            }

            // Trim string values to remove whitespace
            if (typeof fieldData === "string") {
              fieldData = fieldData.trim();
            }

            // Konverter numeriske verdier til tall
            if (
              field.id === "weight" ||
              field.id === "height" ||
              field.id === "width" ||
              field.id === "length"
            ) {
              if (fieldData === "") {
                fieldData = 0;
              } else if (typeof fieldData === "string") {
                const parsed = parseFloat(fieldData);
                fieldData = isNaN(parsed) ? 0 : parsed;
              }
            }

            data[fieldId] = fieldData;
          });

          // Kall submit direkte med de innsamlede verdiene for å unngå race conditions
          handleFormSubmit(data);
        }}
      >
        <StandardForm
          showHeader={true}
          description={t("modals:descriptions.new_product")}
          fields={fields}
          hideButtons={true}
        />

        {/* Legg til toggle-knappen utenfor StandardForm */}
        {props.showSaveToggle && (
          <div className={formFooter}>
            <div className="flex items-center py-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  ref={saveProductRef}
                  type="checkbox"
                  defaultChecked={props.shouldSaveProduct ?? true}
                  className="sr-only peer"
                  onChange={(e) => {
                    // Oppdater toggle-tilstanden og informer parent-komponenten
                    const isChecked = e.target.checked;

                    // Oppdater toggle-label
                    const toggleLabel =
                      document.getElementById("toggle-label-text");
                    if (toggleLabel) {
                      toggleLabel.textContent = isChecked
                        ? t("inventory:save_for_reuse")
                        : t("inventory:one_time_use");
                    }

                    // Informer parent-komponenten om endringen
                    if (props.onSaveToggleChange) {
                      props.onSaveToggleChange(isChecked);
                    }
                  }}
                />
                <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-0 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all dark:border-gray-600 peer-checked:bg-gradient-to-r peer-checked:from-blue-400 peer-checked:to-lgb-blue-300"></div>
                <span
                  id="toggle-label-text"
                  className="ml-4 text-sm font-medium text-gray-900 dark:text-gray-300"
                >
                  {(props.shouldSaveProduct ?? true)
                    ? t("inventory:save_for_reuse")
                    : t("inventory:one_time_use")}
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Legg til knappene nederst */}
        <div className="pt-6 flex gap-4">
          <Button color="primary" type="submit">
            {isNew ? <HiOutlinePlus className="mr-2 h-4 w-4" /> : null}
            {isNew
              ? t("modals:button_texts.product")
              : t("inventory:update_product")}
          </Button>
          <Button color="gray" onClick={props.onCancel}>
            {t("common:close")}
          </Button>
        </div>
      </form>
    </div>
  );
};

export const ProductModal: FC<ProductModalProp> = function (
  props: ProductModalProp,
) {
  const { t } = useTranslation(["common", "inventory", "modals"]);

  return (
    <CustomModal
      isOpen={props.isShowing}
      onClose={props.onCancel}
      title={
        Product.isNew(props.data)
          ? t("inventory:new_product")
          : t("inventory:update_product")
      }
    >
      <ProductForm {...props} />
    </CustomModal>
  );
};
